import { useNavigate, useLocation } from "react-router-dom";
import { SearchX, ShieldOff, LogIn, ArrowLeft, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Iter 120 — Generic 401 / 403 / 404 screen. Shown instead of silently
 * bouncing to /dashboard, which left people guessing when they pasted or
 * bookmarked a URL they can't open (or that no longer exists).
 * Copy is deliberately generic: it never reveals whether a resource exists
 * or which permission is missing.
 */
const VARIANTS = {
  401: {
    icon: LogIn,
    title: "You're signed out",
    body: "Your session has ended or you're not signed in yet. Sign in again and we'll take you straight to the page you asked for.",
    tone: "text-sky-600 dark:text-sky-300 bg-sky-500/10",
  },
  403: {
    icon: ShieldOff,
    title: "You don't have access to this page",
    body: "Your account isn't permitted to open this page. If you reached it from a link someone shared, ask an administrator to grant you access.",
    tone: "text-amber-600 dark:text-amber-300 bg-amber-500/10",
  },
  404: {
    icon: SearchX,
    title: "We couldn't find that page",
    body: "The address you entered doesn't match anything in Conduit. It may have been moved or renamed — try navigating from the menu instead.",
    tone: "text-slate-600 dark:text-slate-300 bg-slate-500/10",
  },
};

export default function ErrorPage({ code = 404, signedIn = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const v = VARIANTS[code] || VARIANTS[404];
  const Icon = v.icon;
  const here = `${location.pathname}${location.search}${location.hash}`;

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4" data-testid={`error-page-${code}`}>
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-10 shadow-sm">
        <div className={`mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl ${v.tone}`}>
          <Icon className="h-6 w-6" />
        </div>
        <p className="font-mono text-xs font-semibold uppercase tracking-widest text-muted-foreground"
          data-testid="error-page-code">Error {code}</p>
        <h1 className="mt-2 font-heading text-2xl font-bold text-foreground" data-testid="error-page-title">
          {v.title}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground" data-testid="error-page-message">{v.body}</p>
        <p className="mt-4 break-all rounded-md bg-muted px-3 py-2 font-mono text-xs text-muted-foreground"
          data-testid="error-page-path">{here}</p>
        <div className="mt-8 flex flex-wrap gap-2">
          {code === 401 || !signedIn ? (
            <Button data-testid="error-page-signin"
              onClick={() => navigate(`/login?next=${encodeURIComponent(here)}`, { replace: true })}
              className="bg-primary text-primary-foreground hover:bg-primary/90">
              <LogIn className="h-4 w-4" /> Sign in
            </Button>
          ) : (
            <Button data-testid="error-page-dashboard" onClick={() => navigate("/dashboard")}
              className="bg-primary text-primary-foreground hover:bg-primary/90">
              <ArrowLeft className="h-4 w-4" /> Back to dashboard
            </Button>
          )}
          {signedIn && code !== 401 && (
            <Button variant="outline" data-testid="error-page-help" onClick={() => navigate("/kb")}>
              <LifeBuoy className="h-4 w-4" /> Knowledge Base
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
